/**
 * Placement maths: base-jack solving, standard composition, module snapping
 * and plank packing. Pure functions — the terrain raycast is injected.
 */

import { BRACKET_STUB, basePFor, CFG, KR, MM, MODS, NODE, nodeY, SPIRE_LENGTHS } from "../data/haki.ts";
import { nearestMod } from "./geometry.ts";
import { isMounted, type Leg, type PlankFamily, type Point, type ScaffoldModel } from "./types.ts";

export type { PlankFamily };

/** How close a coordinate must be to snap onto another standard's axis, metres. */
const SNAP_AXIS_TOLERANCE = 0.15;

/** How close a distance must be to a module before it snaps to it, millimetres. */
const SNAP_MODULE_TOLERANCE = 150;

/** Height above the base node: every segment, plus the bracket stub under a mounted standard. */
export function legHeight(leg: Leg): number {
  const stub = isMounted(leg) ? BRACKET_STUB : 0;

  return stub + leg.segments.reduce((total, mm) => total + mm, 0) * MM;
}

/**
 * The node level a standard starts at. A mounted standard's base is its
 * bracket's cup level; a ground standard's is whatever its jack reaches,
 * solved from the cached ground height or the terrain when not yet rendered.
 */
export function baseLevel(leg: Leg, groundAt: (x: number, z: number) => number): number {
  return leg.mountP ?? leg.baseP ?? basePFor(leg.gy ?? groundAt(leg.x, leg.z));
}

/** Split a total height into whole standards, longest first. */
export function composeSpires(totalMm: number): number[] {
  const parts: number[] = [];
  let rest = Math.round(totalMm / 500) * 500;

  for (const length of SPIRE_LENGTHS) {
    while (rest >= length) {
      parts.push(length);
      rest -= length;
    }
  }

  return parts;
}

/** Node levels this leg carries a usable cup group at. */
export function legNodes(leg: Leg): number[] {
  const nodes: number[] = [];
  const base = leg.baseP ?? 0;
  const top = legHeight(leg);

  for (let p = base; (p - base) * NODE <= top - KR - 0.02; p++) {
    nodes.push(p);
  }

  return nodes;
}

/** Which segment of this standard holds cup group `p`? */
export function segmentAtNode(leg: Leg, p: number, groundAt: (x: number, z: number) => number): number {
  const base = baseLevel(leg, groundAt);
  const target = nodeY(p) + KR;
  let y = nodeY(base) + (isMounted(leg) ? BRACKET_STUB : 0);

  for (const mm of leg.segments) {
    if (target <= y + mm * MM + 1e-6) {
      return mm;
    }
    y += mm * MM;
  }

  return leg.segments[leg.segments.length - 1] ?? 3000;
}

export type AutoSpirResult = Readonly<{ segs: number[]; reaches: boolean }>;

/**
 * The standard placed automatically at a free beam end: ONE standard, never
 * spliced — the same length as the segment you started from if it reaches,
 * otherwise the shortest single that does. If even a 3000 falls short, a 3000
 * is set and the user splices the rest.
 */
export function autoSpir(
  p: number,
  gy: number | undefined,
  startLeg: Leg | null,
  groundAt: (x: number, z: number) => number,
): AutoSpirResult {
  const baseP = gy === undefined ? 0 : basePFor(gy);
  const need = Math.max(0, (p - baseP) * NODE + KR + 0.05);
  const same = startLeg ? segmentAtNode(startLeg, p, groundAt) : null;

  if (same !== null && same * MM >= need) {
    return { segs: [same], reaches: true };
  }

  const ascending = [...SPIRE_LENGTHS].sort((a, b) => a - b);
  const single = ascending.find((length) => length * MM >= need);

  return single ? { segs: [single], reaches: true } : { segs: [3000], reaches: false };
}

/**
 * Legs the snap may magnetise to.
 *
 * `exclude` holds leg keys that must not attract — when a whole scaffold is
 * being dragged, its own standards would otherwise snap it to itself.
 */
function* snapCandidates(model: ScaffoldModel, exclude?: ReadonlySet<string>): Generator<Leg> {
  for (const [key, leg] of model.legs) {
    if (!exclude?.has(key)) {
      yield leg;
    }
  }
}

/** The leg whose given axis is nearest `value`, within the snap tolerance. */
function nearestAxisLeg(
  model: ScaffoldModel,
  axis: "x" | "z",
  value: number,
  exclude?: ReadonlySet<string>,
): Leg | null {
  let best: Leg | null = null;

  for (const leg of snapCandidates(model, exclude)) {
    const delta = Math.abs(leg[axis] - value);
    if (delta < SNAP_AXIS_TOLERANCE && (!best || delta < Math.abs(best[axis] - value))) {
      best = leg;
    }
  }

  return best;
}

/**
 * Pull `value` onto a module distance from the first leg sharing `fixed` on the
 * other axis. Returns `value` unchanged when nothing is within tolerance.
 */
function snapToModule(
  model: ScaffoldModel,
  along: "x" | "z",
  fixed: "x" | "z",
  value: number,
  fixedValue: number,
  exclude?: ReadonlySet<string>,
): number {
  for (const leg of snapCandidates(model, exclude)) {
    if (Math.abs(leg[fixed] - fixedValue) > 1e-6) {
      continue;
    }

    const d = value - leg[along];
    const mod = nearestMod(Math.abs(d) * 1000);
    if (Math.abs(Math.abs(d) * 1000 - mod) < SNAP_MODULE_TOLERANCE) {
      return leg[along] + Math.sign(d) * mod * MM;
    }
  }

  return value;
}

/**
 * Magnetise a click to the existing grid: first onto another standard's X or Z
 * axis within 150 mm, then to a module distance along that axis.
 */
export function snapXZ(
  model: ScaffoldModel,
  x: number,
  z: number,
  enabled: boolean,
  exclude?: ReadonlySet<string>,
): Point {
  if (!enabled) {
    return { x: +x.toFixed(3), z: +z.toFixed(3) };
  }

  const axisX = nearestAxisLeg(model, "x", x, exclude);
  const axisZ = nearestAxisLeg(model, "z", z, exclude);
  let sx = axisX ? axisX.x : x;
  let sz = axisZ ? axisZ.z : z;

  if (axisX) {
    sz = snapToModule(model, "z", "x", sz, sx, exclude);
  }
  if (axisZ) {
    sx = snapToModule(model, "x", "z", sx, sz, exclude);
  }

  return { x: +sx.toFixed(3), z: +sz.toFixed(3) };
}

/**
 * Fill a bay width with ALP planks. "mix" maximises coverage, preferring the
 * fewest boards when two combinations cover the same width.
 */
export function packPlanks(widthMm: number, family: PlankFamily): number[] {
  const usable = widthMm - 90;
  const fill = (available: number, widths: number[]): number[] => {
    const out: number[] = [];
    let rest = available;
    for (const width of widths) {
      while (rest >= width) {
        out.push(width);
        rest -= width;
      }
    }

    return out;
  };

  if (family === "170") {
    return fill(usable, [170]);
  }
  if (family === "230") {
    return fill(usable, [230]);
  }

  let best: { widths: number[]; cov: number } | null = null;
  for (let wide = Math.floor(usable / 230); wide >= 0; wide--) {
    const narrow = Math.floor((usable - wide * 230) / 170);
    const cov = wide * 230 + narrow * 170;
    const widths = [...new Array(wide).fill(230), ...new Array(narrow).fill(170)];
    if (!best || cov > best.cov || (cov === best.cov && widths.length < best.widths.length)) {
      best = { widths, cov };
    }
  }

  return best ? best.widths : [];
}

/** Base-jack extension for a leg, always within the jack's 55–570 mm range. */
export function jackExtension(leg: Leg): number {
  const gy = leg.gy ?? 0;
  const baseP = leg.baseP ?? basePFor(gy);

  return Math.round((nodeY(baseP) - gy) * 1000);
}

export { CFG, MODS };
